// src/utils/visitTracker.js
class VisitTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
    this.visitedPages = new Set();
    this.pendingVisits = new Set(); // 진행 중인 방문 기록 추적
  }

  // 세션 ID 생성 또는 조회
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('worldland_session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem('worldland_session_id', sessionId);
    }
    return sessionId;
  }

  // 세션 ID 생성
  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // 방문 기록
  async recordVisit(pageName, userId = null) {
    try {
      // 같은 세션에서 이미 방문한 페이지는 중복 기록하지 않음
      const visitKey = `${pageName}_${this.sessionId}`;
      if (this.visitedPages.has(visitKey)) {
        return { success: true, cached: true };
      }

      // 이미 진행 중인 요청이 있으면 건너뛰기
      if (this.pendingVisits.has(visitKey)) {
        console.log(`📊 방문 기록 진행 중: ${pageName} (중복 방지)`);
        return { success: true, pending: true };
      }

      // 진행 중인 요청으로 표시
      this.pendingVisits.add(visitKey);

      console.log(`📊 방문 기록 시작: ${pageName}`);

      const response = await fetch(`${this.serverUrl}/api/visit/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          pageName,
          userId,
          sessionId: this.sessionId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        this.visitedPages.add(visitKey);
        console.log(`📊 방문 기록 완료: ${pageName}`);
      } else {
        console.error(`📊 방문 기록 실패: ${pageName}`, result.error);
      }

      // 진행 중인 요청에서 제거
      this.pendingVisits.delete(visitKey);

      return result;

    } catch (error) {
      console.error('방문 기록 오류:', error);
      
      // 오류 시에도 진행 중인 요청에서 제거
      const visitKey = `${pageName}_${this.sessionId}`;
      this.pendingVisits.delete(visitKey);
      
      return { success: false, error: error.message };
    }
  }

  // 방문 기록 강제 실행 (중복 체크 무시)
  async forceRecordVisit(pageName, userId = null) {
    try {
      const response = await fetch(`${this.serverUrl}/api/visit/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          pageName,
          userId,
          sessionId: this.sessionId
        })
      });

      return await response.json();

    } catch (error) {
      console.error('강제 방문 기록 오류:', error);
      return { success: false, error: error.message };
    }
  }

  // 페이지 통계 조회
  async getPageStats(pageName) {
    try {
      const response = await fetch(`${this.serverUrl}/api/visit/stats/${pageName}`, {
        method: 'GET',
        credentials: 'include'
      });

      return await response.json();

    } catch (error) {
      console.error('페이지 통계 조회 오류:', error);
      return { success: false, error: error.message };
    }
  }

  // 모든 페이지 통계 조회
  async getAllPagesStats() {
    try {
      const response = await fetch(`${this.serverUrl}/api/visit/stats`, {
        method: 'GET',
        credentials: 'include'
      });

      return await response.json();

    } catch (error) {
      console.error('전체 페이지 통계 조회 오류:', error);
      return { success: false, error: error.message };
    }
  }

  // 기간별 통계 조회
  async getPageStatsInPeriod(pageName, days = 7) {
    try {
      const response = await fetch(`${this.serverUrl}/api/visit/stats/${pageName}/period?days=${days}`, {
        method: 'GET',
        credentials: 'include'
      });

      return await response.json();

    } catch (error) {
      console.error('기간별 통계 조회 오류:', error);
      return { success: false, error: error.message };
    }
  }

  // 방문 기록 초기화 (개발/테스트용)
  clearVisitCache() {
    this.visitedPages.clear();
    this.pendingVisits.clear();
    console.log('📊 방문 기록 캐시 초기화');
  }
}

// 싱글톤 인스턴스
const visitTracker = new VisitTracker();

export default visitTracker;